import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Button,
  Switch,
  Chip,
  Divider,
  InputAdornment,
} from "@mui/material";

import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassBottomOutlinedIcon from "@mui/icons-material/HourglassBottomOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import { errorTost, successTost } from '../../Services/Toast'

const steps = [
  "Plan Details",
  "POP Validity",
  "Scope Type",
  "Scope Selection",

  "Calculation Rules",
  "Eligibility",
  "Conflict Settings",
  "CP Sign-Off",
  "Review",
];

const CreatePopPlanStep9 = ({ data, setData, onBack, onNext }) => {
const handleNextClick = () => {
  if (data.requireSignOff) {
    if (!data.signOffStart || !data.signOffEnd) {
      errorTost("Please select both start and end dates for sign-off.");
      return;
    }
  }

  onNext();
};

return (
  <Box sx={{ p: 1, minHeight: "100vh" }}>
    {/* Back */}
    <Typography
      variant="body2"
      sx={{ mb: 2, cursor: "pointer", color: "text.secondary" }}
      onClick={onBack}
    >
      ← Back to POPs
    </Typography>

    {/* Stepper */}
    <Stepper activeStep={7} alternativeLabel sx={{ mb: 3 }}>
      {steps.map((label) => (
        <Step key={label}>
          <StepLabel
            StepIconProps={{
              sx: {
                width: 36,
                height: 36,
                fontSize: "1rem",
                "&.Mui-completed": { color: "#2563EB" },
                "&.Mui-active": {
                  color: "#2563EB",
                  transform: "scale(1.15)",
                },
              },
            }}
            sx={{
              "& .MuiStepLabel-label": {
                fontSize: "0.85rem",
                mt: 1,
              },
            }}
          >
            {label}
          </StepLabel>
        </Step>
      ))}
    </Stepper>

    <Typography fontSize={14} color="text.secondary" mb={3}>
      Step 8 of 9: <b>CP Sign-Off</b> — Configure sign-off
    </Typography>

    {/* MAIN CARD */}
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent>
        {/* TITLE */}
        <Box display="flex" alignItems="center" mb={3}>
          <GroupOutlinedIcon sx={{ color: "#c4d9f5", mr: 1 }} />
          <Typography fontWeight={600} fontSize={18}>
            CP Sign-Off Configuration
          </Typography>
        </Box>

        {/* REQUIRE SIGN OFF */}
        <Box
          sx={{
            border: "1px solid #E2E8F0",
            borderRadius: 2,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box>
            <Typography fontWeight={500}>Require Sign-Off</Typography>
            <Typography fontSize={13} color="text.secondary">
              CPs must sign this POP to participate
            </Typography>
          </Box>

          <Switch
            checked={!!data.requireSignOff}
            onChange={(e) => {
              const checked = e.target.checked;

              setData((prev) => ({
                ...prev,
                requireSignOff: checked,
                // Reset dates if turned OFF
                signOffStart: checked ? prev.signOffStart : "",
                signOffEnd: checked ? prev.signOffEnd : "",
              }));
            }}
          />
        </Box>

        {/* DATE FIELDS */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 3,
            maxWidth: 720,
            mb: 3,
          }}
        >
          {/* START DATE */}
          <Box>
            <Typography fontWeight={500} mb={1}>
              Sign-Off Window Start
            </Typography>

            <TextField
              fullWidth
              size="small"
              type="date"
              disabled={!data.requireSignOff} // Disable if OFF
              value={
                data.signOffStart
                  ? new Date(data.signOffStart).toISOString().split("T")[0]
                  : ""
              }
              onChange={(e) =>
                setData((prev) => ({
                  ...prev,
                  signOffStart: e.target.value,
                }))
              }
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          {/* END DATE */}
          <Box>
            <Typography fontWeight={500} mb={1}>
              Sign-Off Window End
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="date"
              disabled={!data.requireSignOff} // Disable if OFF
              value={
                data.signOffEnd
                  ? new Date(data.signOffEnd).toISOString().split("T")[0]
                  : ""
              }
              onChange={(e) =>
                setData((prev) => ({
                  ...prev,
                  signOffEnd: e.target.value,
                }))
              }
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: data.signOffStart, // Ensure the minimum date is the selected start date
              }}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>

    {/* FOOTER */}
    <Box display="flex" justifyContent="space-between" mt={3}>
      <Button variant="outlined" onClick={onBack}>
        ← Back
      </Button>

      <Button
        variant="contained"
        onClick={handleNextClick}
        sx={{ bgcolor: "#4F6EF7", textTransform: "none" }}
        disabled={data.requireSignOff && (!data.signOffStart || !data.signOffEnd)}
      >
        Next →
      </Button>
    </Box>
  </Box>
);
};

export default CreatePopPlanStep9;
